import { Component, Inject, QueryList, ViewChildren } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  AppReference,
  DisponibiliteEpargne,
  DisponibiliteEpargneOption,
  FicheConseil,
  HorizonPlacementEnum,
  InvestisseurFicheObjectif,
  ToleranceRisque,
  ToleranceRisqueOption,
  ObjectifReferentielFiche,
} from '@lib/models/generated/graphql';
import { AuthService } from '@lib/services/auth-service.service';
import { ObjectifsService } from '@lib/services/objectifs.service';
import { OuiSelectOption, OuiValidationError } from 'omnium-ui/form-field';
import { AbstractStepComponent } from 'src/app/components/declare-fiche-conseil/steps/abstract-step.component';
import {
  DEFAULT_DISPONIBILITE_OPTIONS,
  DEFAULT_TOLERANCE_RISQUE_OPTIONS,
} from 'src/app/components/declare-fiche-conseil/steps/exigences-step/default-option';
import { ObjectifCheckboxComponent } from 'src/app/components/declare-fiche-conseil/steps/exigences-step/objectif-checkbox/objectif-checkbox.component';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';
import { FormConfigService } from 'src/app/services/form-config.service';
import { FieldFormControl } from 'src/fieldFormControl/FieldFormControl';
import { ObjectifsValidatorKey } from 'src/fieldFormControl/validators/objectifValidator';

export type KycObjectifInvestisseur = {
  objectif: ObjectifReferentielFiche;
  isKYCSelected: boolean;
};

export type KycObjectifCategory = {
  categoryLabel: string;
  objectifs: KycObjectifInvestisseur[];
  priority: number;
};

type FicheConseilWithArbitrageDuplicationContext = FicheConseil & {
  arbitrageDuplicationContext?: {
    sourceFicheId?: string | null;
  } | null;
};

@Component({
  selector: 'app-exigences-step',
  templateUrl: './exigences-step.component.html',
  styleUrls: ['./exigences-step.component.scss'],
})
export class ExigencesStepComponent extends AbstractStepComponent {
  @ViewChildren(ObjectifCheckboxComponent) objectifsCheckboxes: QueryList<ObjectifCheckboxComponent>;

  horizonOptions: OuiSelectOption<HorizonPlacementEnum>[] = [
    { label: '0 à 3 ans', value: HorizonPlacementEnum.De0A3Ans },
    { label: '3 à 5 ans', value: HorizonPlacementEnum.De3A5Ans },
    { label: '5 à 8 ans', value: HorizonPlacementEnum.De5A8Ans },
    { label: '8 à 10 ans', value: HorizonPlacementEnum.De8A10Ans },
    { label: '> 10 ans', value: HorizonPlacementEnum.Sup10Ans },
  ];

  /**
   * The "Retraite" option is kept separate from the main horizonOptions list
   * so that it can be inserted conditionally (e.g. at the beginning for PER envelopes).
   *  */
  private readonly retraiteOption: OuiSelectOption<HorizonPlacementEnum> = {
    label: 'Retraite',
    value: HorizonPlacementEnum.Retraite,
  };

  horizonControl: FieldFormControl<HorizonPlacementEnum | null, HorizonPlacementEnum | null>;
  horizonJustificationControl: FieldFormControl<string | null, string | null>;
  isHorizonOtherThanRetraite: boolean;
  retraiteControl: FieldFormControl<string | null, number | null>;

  leftColumnCategories: KycObjectifCategory[] = [];
  rightColumnCategories: KycObjectifCategory[] = [];
  isLoading: boolean = true;
  hasDataBeenUpdated: boolean = false;
  dispoEpargneControl: FieldFormControl<DisponibiliteEpargne | null, DisponibiliteEpargne | null>;

  toleranceRisque = ToleranceRisque;
  toleranceRisqueControl: FieldFormControl<ToleranceRisque | null, ToleranceRisque | null>;
  environment: any;
  retraiteNumberStep: number = 0.1;
  allReferentielObjectifs: ObjectifReferentielFiche[] = [];

  toggleObjectifControl: FormControl<boolean | null> = new FormControl(false);
  disponibiliteOptions: DisponibiliteEpargneOption[] = DEFAULT_DISPONIBILITE_OPTIONS;
  toleranceRisqueOptions: ToleranceRisqueOption[] = DEFAULT_TOLERANCE_RISQUE_OPTIONS;

  objectifsGroup = new FormGroup({}, { validators: [this.auMoinsUnValidatorFromConfig()] });
  showAumoinsUnError: boolean = false;

  constructor(
    ficheConseilService: FicheConseilService,
    protected formConfigService: FormConfigService,
    protected objectifsService: ObjectifsService,
    private fb: FormBuilder,
    protected authService: AuthService,
    @Inject('environment')
    environment: any
  ) {
    super(ficheConseilService);

    this.environment = environment;
    this.horizonControl = new FieldFormControl<HorizonPlacementEnum | null, HorizonPlacementEnum | null>({
      formConfigService: formConfigService,
      ficheConseilService: ficheConseilService,
      dataKey: 'horizonPlacement.value',
      defaultValue: null,
      options: {
        validators: [Validators.required],
      },
    });

    this.horizonJustificationControl = new FieldFormControl<string | null, string | null>({
      formConfigService: formConfigService,
      ficheConseilService: ficheConseilService,
      dataKey: 'horizonPlacement.justification',
      defaultValue: null,
      options: {
        validators: [Validators.required],
      },
    });

    this.dispoEpargneControl = new FieldFormControl<DisponibiliteEpargne | null, DisponibiliteEpargne | null>({
      formConfigService: formConfigService,
      ficheConseilService: ficheConseilService,
      dataKey: 'disponibiliteEpargne',
      defaultValue: null,
      options: {
        validators: [Validators.required],
      },
    });

    this.toleranceRisqueControl = new FieldFormControl<ToleranceRisque | null, ToleranceRisque | null>({
      formConfigService: formConfigService,
      ficheConseilService: ficheConseilService,
      dataKey: 'toleranceRisque',
      defaultValue: null,
      options: {
        validators: [Validators.required],
      },
    });

    this.retraiteControl = new FieldFormControl<string | null, number | null>({
      formConfigService: formConfigService,
      ficheConseilService: ficheConseilService,
      dataKey: 'horizonPlacement.horizonRetraite',
      defaultValue: null,
      ficheDataToFieldDataWrapper: ficheData => {
        return ficheData?.toString() ?? null;
      },
      fieldDataToFicheDataWrapper: fieldData => {
        if (fieldData) {
          return parseFloat(fieldData);
        }
        return null;
      },
      options: {
        validators: [Validators.required],
      },
    });
  }
  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }
  override ngOnInit(): void {
    super.ngOnInit();
    this.setDisponibiliteEpargneOptions();
    this.setToleranceRisqueOptions();
    if (
      this.currentFicheConseil &&
      (!this.currentFicheConseil?.objectifs || this.currentFicheConseil.objectifs.length === 0)
    ) {
      this.updateData();
      this.objectifsService.showKYCSelectedOnly = true;
    }
    this.objectifsService.getAllObjectifs().then(objectifs => {
      this.allReferentielObjectifs = objectifs;

      if (this.currentFicheConseil) {
        this.setKycObjectifCategories(this.allReferentielObjectifs, this.currentFicheConseil.objectifs ?? []);
      }

      this.isLoading = false;
    });

    /** For PER envelopes, the RETRAITE option should appear first, as it's the most common case */
    this.horizonOptions = this.horizonJustificationControl?.isVisible()
      ? [this.retraiteOption, ...this.horizonOptions]
      : [...this.horizonOptions, this.retraiteOption];
  }

  ngAfterViewInit() {
    this.syncObjectifControls();
    this.syncChildControls();
    this.objectifsCheckboxes.changes.subscribe(() => this.syncObjectifControls());
    this.objectifsGroup.statusChanges.subscribe(() => this.syncChildControls());
  }

  private syncObjectifControls() {
    Object.keys(this.objectifsGroup.controls).forEach(key => this.objectifsGroup.removeControl(key));

    this.objectifsCheckboxes.forEach(cb => {
      const code = cb.objectifReferentiel.code;
      this.objectifsGroup.addControl(code, cb.control);
    });

    this.objectifsGroup.updateValueAndValidity({ onlySelf: true });
  }

  private syncChildControls() {
    const auMoinsUnError = this.objectifsGroup.hasError(ObjectifsValidatorKey.auMoinsUn);

    this.objectifsCheckboxes.forEach(cb => {
      if (this.getCodesByValidatorKey(ObjectifsValidatorKey.auMoinsUn).includes(cb.objectifReferentiel.code)) {
        cb.setGroupError(auMoinsUnError);
      }
    });
  }

  private auMoinsUnValidatorFromConfig(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const selected = this.getCodesByValidatorKey(ObjectifsValidatorKey.auMoinsUn).some(
        code => group.get(code)?.value
      );
      return selected ? null : { objectifAuMoinsUn: true };
    };
  }

  setDisponibiliteEpargneOptions() {
    if (
      this.currentFicheConseil?.disponibiliteEpargneOptions &&
      this.currentFicheConseil?.disponibiliteEpargneOptions.length > 0
    ) {
      this.disponibiliteOptions = this.currentFicheConseil?.disponibiliteEpargneOptions;
    } else {
      this.disponibiliteOptions = DEFAULT_DISPONIBILITE_OPTIONS;
      this.ficheConseilService.setDefaultDisponibiliteEpargneOptions();
    }
  }

  setToleranceRisqueOptions() {
    if (
      this.currentFicheConseil?.toleranceRisqueOptions &&
      this.currentFicheConseil?.toleranceRisqueOptions.length > 0
    ) {
      this.toleranceRisqueOptions = this.currentFicheConseil?.toleranceRisqueOptions;
    } else {
      this.toleranceRisqueOptions = DEFAULT_TOLERANCE_RISQUE_OPTIONS;
      this.ficheConseilService.setDefaultToleranceRisqueOptions();
    }
  }
  /**
   * Creates a KycObjectifCategory for each category of objectifs and populates the objectifs for each category
   * with the corresponding objectifs from the investisseur objectifs.
   * The objectifs are sorted by code within each category.
   * The KycObjectifCategories are then distributed into two columns.
   * @param referentielObjectifs All referentiel objectifs
   * @param investisseurObjectifs Investisseur objectifs for a fiche conseil
   */
  setKycObjectifCategories(
    referentielObjectifs: ObjectifReferentielFiche[],
    investisseurObjectifs: InvestisseurFicheObjectif[]
  ): void {
    const kycObjectifCategories: KycObjectifCategory[] = [];
    const objectifCategories = [
      {
        code: 'PATRIMOINE',
        label: 'Patrimoine',
        priority: 1,
      },
      {
        code: 'REVENUS',
        label: 'Revenus',
        priority: 2,
      },
      {
        code: 'FISCALITE',
        label: 'Fiscalité',
        priority: 3,
      },
      {
        code: 'AVENIR',
        label: 'Avenir',
        priority: 4,
      },
    ];

    objectifCategories.forEach(category => {
      const objectifs: ObjectifReferentielFiche[] = referentielObjectifs
        .filter(objectif => objectif.categorieCode === category.code)
        .sort((a, b) => a.code?.localeCompare(b.code));

      kycObjectifCategories.push({
        categoryLabel: category.label,
        objectifs: this.mapObjectifsToKycObjectifs(
          investisseurObjectifs.map(objectif => objectif.objectifCode ?? '-'),
          objectifs
        ),
        priority: category.priority,
      });
    });

    this.distributeObjectifsToColumns(kycObjectifCategories);
  }

  /**
   * Distributes the given KycObjectifCategories into two columns.
   * If showKYCSelectedOnly is true, only categories with selected objectifs in KYC recueil investisseur are shown.
   * The categories are sorted by priority and then distributed equally into two columns.
   * @param allCategories The KycObjectifCategories to be distributed.
   */
  private distributeObjectifsToColumns(allCategories: KycObjectifCategory[]) {
    this.leftColumnCategories = [];
    this.rightColumnCategories = [];
    let selectedCategories = allCategories.sort((a, b) => a.priority - b.priority);

    // if showKYCSelectedOnly is true, only show categories with selected objectifs in KYC recueil investisseur
    if (this.objectifsService.showKYCSelectedOnly) {
      selectedCategories = allCategories.filter(category =>
        category.objectifs.some(objectif => objectif.isKYCSelected)
      );
    }
    for (const category of selectedCategories) {
      if (this.leftColumnCategories.length < 2) {
        this.leftColumnCategories.push(category);
      } else {
        this.rightColumnCategories.push(category);
      }
    }
  }
  onDisplayObjectifsChange() {
    this.objectifsService.showKYCSelectedOnly = !this.objectifsService.showKYCSelectedOnly;
    this.setKycObjectifCategories(this.allReferentielObjectifs, this.currentFicheConseil?.objectifs ?? []);
  }
  public async updateData() {
    const ficheConseil = this.currentFicheConseil;
    if (ficheConseil?.investisseur?.id) {
      //fetch las investisseur objectifs
      let userObjectifs = await this.objectifsService.getInvestisseurObjectifs(
        ficheConseil.investisseur?.id,
        ficheConseil.coInvestisseur?.id
      );
      const userFicheObjectifs: InvestisseurFicheObjectif[] =
        this.objectifsService.wrapInvestisseurObjectifToInvestisseurFicheObjectifs(userObjectifs);
      this.hasDataBeenUpdated = true;

      // transfer selected status
      ficheConseil.objectifs?.forEach(objectif => {
        let userObj = userFicheObjectifs.find(userObjectif => userObjectif.objectifCode === objectif.objectifCode);
        if (userObj && objectif) {
          userObj.isSelected = objectif.isSelected;
        }
      });

      this.ficheConseilService.onFicheConseilDataEdit('objectifs', userFicheObjectifs);
      this.setKycObjectifCategories(this.allReferentielObjectifs, userFicheObjectifs);
    }
  }

  private mapObjectifsToKycObjectifs(
    selectedCodes: string[],
    objectifs: ObjectifReferentielFiche[]
  ): KycObjectifInvestisseur[] {
    return objectifs.map(objectif => {
      return {
        objectif: objectif,
        isKYCSelected: selectedCodes.includes(objectif.code),
      };
    });
  }
  public markAsTouched() {
    this.showAumoinsUnError = true;
    this.horizonControl.markAsTouched();
    this.retraiteControl.markAsTouched();
    this.objectifsCheckboxes.forEach(checkbox => checkbox.markAsTouched());
    this.dispoEpargneControl.markAsTouched();
    this.toleranceRisqueControl.markAsTouched();
    this.horizonJustificationControl.markAsTouched();
  }

  override beforeFicheConseilUpdate(newFicheConseil: FicheConseil | null, previousFicheConseil: FicheConseil | null) {
    // check HorizonPlacement
    if (newFicheConseil?.horizonPlacement && newFicheConseil.horizonPlacement.value != HorizonPlacementEnum.Retraite) {
      this.isHorizonOtherThanRetraite = true;
      return;
    }
    this.isHorizonOtherThanRetraite = false;
  }

  public override isStepValid(): boolean {
    let validity = this.horizonControl.valid;
    if (this.shouldShowRetraiteField()) {
      validity = validity && this.retraiteControl.valid;
    }
    if (this.isHorizonOtherThanRetraite && this.horizonJustificationControl.isVisible()) {
      validity = this.horizonJustificationControl.valid;
    }

    // assert at least one objectif is selected
    let isThereAtLeastOneObjectifSelected = this.objectifsCheckboxes.some(checkbox => checkbox?.control?.value);
    validity = validity && isThereAtLeastOneObjectifSelected;

    // assert there is no error with objectifs
    let isThereObjectifError = this.objectifsCheckboxes.some(checkbox => !!checkbox?.control?.errors);
    validity = validity && !isThereObjectifError;

    // assert there is no error with disponibilité épargne
    let isThereDispoError = this.dispoEpargneControl?.errors;
    validity = validity && !isThereDispoError;

    // assert there is no error with tolerance risque
    let isThereToleranceRisqueError = this.toleranceRisqueControl?.errors;
    validity = validity && !isThereToleranceRisqueError;

    if (this.getCodesByValidatorKey(ObjectifsValidatorKey.auMoinsUn).length > 0) {
      let isAumoinsUnObjectifSelected = this.objectifsCheckboxes.some(
        checkbox =>
          checkbox?.control?.value &&
          this.getCodesByValidatorKey(ObjectifsValidatorKey.auMoinsUn).includes(checkbox?.objectifReferentiel?.code) &&
          checkbox?.control?.value
      );
      validity = validity && isAumoinsUnObjectifSelected;
    }

    return validity;
  }

  private getCodesByValidatorKey(validatorKey: string): string[] {
    return (
      this.formConfigService.currentFormConfigSubject.value?.fieldConfigs
        ?.find(f => f.fieldKey === 'objectifs')
        ?.validators.find(v => v.validatorKey === validatorKey)
        ?.parameters.find(p => p.key === 'codes')
        ?.value?.split(',') ?? []
    );
  }

  shouldShowRetraiteField(): boolean {
    return this.horizonControl.value === HorizonPlacementEnum.Retraite;
  }

  public get DisponibiliteEpargne(): typeof DisponibiliteEpargne {
    return DisponibiliteEpargne;
  }

  protected get dispoEpargneErrors(): OuiValidationError[] {
    let errorsTextFields = this.dispoEpargneControl?.getErrorTextfieldErrors() ?? [];

    return [
      ...errorsTextFields,
      {
        code: 'required',
        message: 'Ce champs est requis !',
      },
    ];
  }

  protected get requiredErrors(): OuiValidationError[] {
    return [
      {
        code: 'required',
        message: 'Ce champs est requis !',
      },
    ];
  }

  onModifyClick(isCoInvestisseur: boolean) {
    const fiche = this.ficheConseilService?.currentFicheConseilSubject?.value;
    const inv = isCoInvestisseur ? fiche?.coInvestisseur : fiche?.investisseur;
    const cons = this.authService.currentConsultant;

    if (
      inv?.code &&
      (cons?.gerePar === AppReference.Minotaure || cons?.gerePar === AppReference.MinotaureWithWarning)
    ) {
      window.open(this.environment.proxeemaBaseUrl + '/investisseurs/from-list/' + inv.code, '_blank');
    }
  }

  protected get isPrefilledFromAnotherFiche(): boolean {
    return !!(this.currentFicheConseil as FicheConseilWithArbitrageDuplicationContext | null)
      ?.arbitrageDuplicationContext?.sourceFicheId;
  }
}
